import DeleteIcon from "@mui/icons-material/DeleteOutlineRounded";
import { Button, DialogContent, DialogTitle, TextField, Typography } from "@mui/material";
import { useState } from "react";
import { withErrorHandling } from "@entrilia/shared/api/axiosHelper";
import DialogButtons from "@entrilia/shared/components/DialogButtons";
import DialogCloseButton from "@entrilia/shared/components/DialogCloseButton";
import SmallDialog from "@entrilia/shared/components/SmallDialog";
import { useNotificationContext } from "@entrilia/shared/contexts/NotificationContext";
import { logError } from "@entrilia/shared/logging";
import type { ValidationResult } from "@entrilia/shared/utilities/validators";
import {
  combineValidators,
  maxCharactersValidator,
  requiredValidator,
  uniqueValidator,
} from "@entrilia/shared/utilities/validators";
import adminApi from "../../../../../../api/adminApi";
import type { Category } from "../../../../../../api/types/accessTypes";
import DeleteCategoryConfirmationDialog from "./DeleteCategoryConfirmationDialog";

interface Props {
  editedCategory?: Category;
  existingCategoryNames: string[];
  onCreated: () => void;
  onUpdated: () => void;
  onDeleted: (categoryId: string) => void;
  onClose: () => void;
}

interface FormState {
  name: string;
  validationResult: ValidationResult;
}

const maxNameLength = 50;

const createFundraisingCategory = withErrorHandling(adminApi.createFundraisingCategory);
const updateFundraisingCategory = withErrorHandling(adminApi.updateFundraisingCategory);

const EditCategoryDialog = ({
  onCreated,
  editedCategory,
  existingCategoryNames,
  onUpdated,
  onDeleted,
  onClose,
}: Props) => {
  const { sendNotificationError } = useNotificationContext();

  const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false);

  const [formState, setFormState] = useState<FormState>({
    name: editedCategory?.name ?? "",
    validationResult: { isValid: !!editedCategory?.name, error: "" },
  });

  const [isSaving, setIsSaving] = useState(false);

  const isNewCategory = !editedCategory;

  const handleNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const validate = combineValidators(
      requiredValidator,
      maxCharactersValidator(maxNameLength),
      uniqueValidator("An access category with this name already exists", existingCategoryNames)
    );

    setFormState((prev) => ({
      ...prev,
      name: e.target.value,
      validationResult: validate(e.target.value),
    }));
  };

  const handleCreate = async () => {
    setIsSaving(true);
    const [, error] = await createFundraisingCategory({ name: formState.name });
    setIsSaving(false);

    if (error) {
      logError(error, "[EditCategoryDialog] createFundraisingCategory");
      sendNotificationError(error.message);
    } else {
      onCreated();
      onClose();
    }
  };

  const handleUpdate = async () => {
    if (!editedCategory) {
      return;
    }

    setIsSaving(true);
    const [, error] = await updateFundraisingCategory(editedCategory.id, { name: formState.name });
    setIsSaving(false);

    if (error) {
      logError(error, "[EditCategoryDialog] updateFundraisingCategory");
      sendNotificationError(error.message);
    } else {
      onUpdated();
      onClose();
    }
  };

  const handleSubmit = async () => {
    if (isNewCategory) {
      await handleCreate();
    } else {
      await handleUpdate();
    }
  };

  const handleDeleteRequest = () => {
    setShowDeleteConfirmation(true);
  };

  const handleDeleted = () => {
    setShowDeleteConfirmation(false);

    if (editedCategory) {
      onDeleted(editedCategory.id);
    }

    onClose();
  };

  const isDirty = formState.name.trim() !== (editedCategory?.name ?? "");

  return (
    <>
      <SmallDialog open onClose={onClose}>
        <DialogTitle>{isNewCategory ? "Add Category" : "Edit Category"}</DialogTitle>
        <DialogCloseButton onClick={onClose} />

        <DialogContent>
          <TextField
            fullWidth
            label="Name"
            variant="outlined"
            value={formState.name}
            onChange={handleNameChange}
            helperText={formState.validationResult.error}
            error={!!formState.validationResult.error}
            slotProps={{
              htmlInput: { maxLength: maxNameLength },
              input: {
                endAdornment: (
                  <Typography variant="caption" color="textSecondary">
                    {maxNameLength - formState.name.length}
                  </Typography>
                ),
              },
            }}
          />
        </DialogContent>

        <DialogButtons
          onCancel={onClose}
          startAction={
            <Button
              variant="outlined"
              color="error"
              onClick={handleDeleteRequest}
              startIcon={<DeleteIcon color="error" />}
            >
              Delete
            </Button>
          }
        >
          <Button
            loading={isSaving}
            variant="contained"
            autoFocus
            disabled={!formState.validationResult.isValid || !isDirty}
            onClick={handleSubmit}
          >
            {isNewCategory ? "Add" : "Save"}
          </Button>
        </DialogButtons>
      </SmallDialog>
      {showDeleteConfirmation && editedCategory && (
        <DeleteCategoryConfirmationDialog
          categoryId={editedCategory.id}
          categoryName={editedCategory.name}
          onClose={() => setShowDeleteConfirmation(false)}
          onDeleted={() => handleDeleted()}
        />
      )}
    </>
  );
};

export default EditCategoryDialog;
